import { DetailedHTMLProps, FC, HTMLAttributes, memo, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useMedia } from 'react-use';

import { getIsMobile } from '~/features/navigation/components/N20MainMenu/components/helpers';
import styles from '~/features/navigation/components/N20MainMenu/components/MainMenuNav/mainMenuNav.module.css';
import { transformTrackingData } from '~/features/navigation/components/N20MainMenu/components/MainMenuNav/transformTrackingData.helper';
import MainMenuToggle from '~/features/navigation/components/N20MainMenu/components/MainMenuToggle/MainMenuToggle';
import { COLLAPSE_EVENT_NAME } from '~/features/navigation/components/N20MainMenu/N20MainMenu';
import useMainMenuState from '~/features/navigation/components/N20MainMenu/useMainMenuState';
import { CeNavigationEvent, CeNavigationHierarchy } from '~/features/tracking/model';
import { pushToDataLayer } from '~/features/tracking/utils/pushToDataLayer';
import ChevronDownIcon from '~/icons/chevron-down.svg';
import { ILink, IMultiLevelNavigationNode } from '~/lib/data-contract';
import Link from '~/shared/components/Link/Link';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';
import { lgCondition } from '~/theme/lib/breakpoints';

interface MainMenuSubmenuProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	subMenu: IMultiLevelNavigationNode[];
	level: number;
	isExpanded: boolean;
	disableCollapse?: boolean;
	parentLink?: ILink;
	trackingHierarchy: Partial<CeNavigationHierarchy> | undefined;
	triggerCollapseCallback(): void;
}

const maxAllowedExpansionLevel = 3;

const MainMenuSubmenu: FC<MainMenuSubmenuProps> = memo(
	({ subMenu, level, isExpanded, className, disableCollapse, triggerCollapseCallback, parentLink, trackingHierarchy = { firstLevel: '' }, ...rest }) => {
		const [activeItem, setActiveItem] = useState<string | undefined>();
		const router = useRouter();
		const { translate } = useTranslation();
		const containerRef = useRef<HTMLDivElement>(null);
		const { setMenuIsOpen } = useMainMenuState((state) => ({
			setMenuIsOpen: state.setMenuIsOpen,
		}));
		const isMobile = !useMedia(lgCondition, !getIsMobile());

		const handleLinkTracking = useCallback((data: CeNavigationHierarchy) => {
			pushToDataLayer<Partial<CeNavigationEvent>>({
				event: 'menu_click',
				...transformTrackingData(data),
			});
		}, []);

		const enrichTrackingHierarchy = useCallback(
			(linkText: string): CeNavigationHierarchy => {
				const trackingData = {
					firstLevel: '',
					...(trackingHierarchy || {}),
				};

				switch (level) {
					case 1:
						trackingData.firstLevel = linkText;
						break;
					case 2:
						trackingData.secondLevel = linkText;
						break;
					case 3:
						trackingData.thirdLevel = linkText;
						break;
					case 4:
						trackingData.fourthLevel = linkText;
						break;
				}

				return trackingData;
			},
			[level, trackingHierarchy],
		);

		const handleParentLinkClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
			handleLinkTracking(trackingHierarchy as CeNavigationHierarchy);
		}, [handleLinkTracking, trackingHierarchy]);

		const handleSubLinkClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
			(e) => {
				const currentLink = e.currentTarget;
				const currentID = currentLink.dataset.id;
				const isParent = currentLink.dataset.parent;
				const trackingPayload = enrichTrackingHierarchy(currentLink.dataset.text || '');
				handleLinkTracking(trackingPayload);

				e.preventDefault();
				e.stopPropagation();

				if (currentID && isParent && level < maxAllowedExpansionLevel) {
					const isMobile = getIsMobile();
					setActiveItem((oldItem) => {
						const isTheSame = oldItem === currentID;
						if (!isMobile || !isTheSame) {
							setMenuIsOpen(!isTheSame);
						}
						return isTheSame ? undefined : currentID;
					});
				} else {
					router.push(currentLink.href);
					triggerCollapseCallback();
				}
			},
			[enrichTrackingHierarchy, handleLinkTracking, level, router, setMenuIsOpen, triggerCollapseCallback],
		);

		useEffect(() => {
			const onCollapseMenu = () => {
				setActiveItem(undefined);
			};

			const currentElement = containerRef.current;

			currentElement?.addEventListener(COLLAPSE_EVENT_NAME, onCollapseMenu);

			return () => currentElement?.removeEventListener(COLLAPSE_EVENT_NAME, onCollapseMenu);
		}, []);

		const submenuCount = subMenu?.length || 0;

		return (
			<div
				data-open={isExpanded}
				data-submenu
				data-level={level}
				className={clsx(
					styles.submenuWrapper,
					{
						open: isExpanded,
						disableCollapse,
						[styles.hasManyChildren]: subMenu?.length > 12,
						[styles.noSubChildren]: level > maxAllowedExpansionLevel || !subMenu?.some((sm) => sm.children?.length),
					},
					className,
				)}
				onClick={(e) => e.stopPropagation()}
				style={{ '--columns': submenuCount >= 9 ? 4 : submenuCount >= 6 ? 3 : submenuCount >= 2 ? 2 : 1 } as React.CSSProperties}
				ref={containerRef}
				role="menu"
				aria-hidden={!isExpanded}
				{...rest}
			>
				{level === 3 && (
					<div className={styles.closeSubmenuWrapper}>
						<MainMenuToggle onClick={triggerCollapseCallback} />
					</div>
				)}

				<ul>
					{parentLink && (
						<li>
							<Link
								href={parentLink.url}
								data-id={parentLink.id}
								data-direct
								data-text={translate('navigation.view-parent', { parent: parentLink.text })}
								onClick={handleParentLinkClick}
								className={clsx(styles.submenuLinkEntry, styles.seeAllLink, {
									[styles.seeAllLinkHidden]: (!disableCollapse && !isExpanded) || (!isMobile && level > 3),
									[styles.active]: router.asPath === parentLink.url,
								})}
								tabIndex={isExpanded || disableCollapse ? undefined : -1}
							>
								<span>{translate('navigation.view-parent', { parent: parentLink.text })}</span>
							</Link>
						</li>
					)}

					{subMenu?.map(({ link, children }, index) => {
						const hasChildren = level <= maxAllowedExpansionLevel && Boolean(children && children.length > 0);
						const subExpanded = (hasChildren && link.id === activeItem) || (!isMobile && isExpanded && level >= 3);
						const disableCollapse = !isMobile && level === 1 && index === 0;

						const trackingData = enrichTrackingHierarchy(link.text);

						return (
							<li key={link.id || index}>
								<a
									href={link.url}
									data-id={link.id}
									id={link.id}
									data-parent={hasChildren || undefined}
									data-text={link.text}
									onClick={handleSubLinkClick}
									className={clsx(styles.submenuLinkEntry, {
										[styles.singleParent]: !hasChildren,
										[styles.accordionHeader]: hasChildren,
										[styles.hideOnMobile]: disableCollapse,
										[styles.active]: (level > 1 && router.asPath.includes(link.url)) || subExpanded,
									})}
									aria-controls={hasChildren && level < maxAllowedExpansionLevel ? `${link.id}-sub` : undefined}
									tabIndex={isExpanded ? undefined : -1}
								>
									<span>{link.text}</span>
									{hasChildren && (
										<span
											data-target-level={level + 1}
											className={clsx(styles.accordionToggleIcon, { [styles.open]: subExpanded })}
										>
											<ChevronDownIcon />
										</span>
									)}
								</a>

								{hasChildren && (
									<MainMenuSubmenu
										subMenu={children!}
										level={level + 1}
										isExpanded={subExpanded}
										disableCollapse={disableCollapse}
										parentLink={link}
										// Prop drilling is usually stupid, but right here, there's just the one, and I'm not gonna set up
										// a whole-ass context for it.
										triggerCollapseCallback={triggerCollapseCallback}
										trackingHierarchy={trackingData}
										id={`${link.id}-sub`}
										aria-labelledby={`${link.id}`}
									/>
								)}
							</li>
						);
					})}
				</ul>
			</div>
		);
	},
);
MainMenuSubmenu.displayName = 'MainMenuSubmenu';

export default MainMenuSubmenu;
